import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type BadgeTone = 'ok' | 'warn' | 'bad' | 'mute' | 'info';

const TONE: Record<BadgeTone, string> = {
  ok: 'bg-[var(--accent-soft)] text-[var(--credit)]',
  warn: 'bg-[var(--warn-soft)] text-[var(--warn)]',
  bad: 'bg-[var(--debit-soft)] text-[var(--debit)]',
  mute: 'bg-[var(--line-2)] text-[var(--ink-3)]',
  info: 'bg-[var(--surface-2)] text-[var(--ink-2)] border border-[var(--line)]',
};

/**
 * Document status → tone.
 *
 * Kept as data so a status string coming straight from the API renders
 * consistently everywhere, rather than each screen inventing its own mapping.
 */
export const STATUS_TONE: Record<string, BadgeTone> = {
  // Settled / complete
  POSTED: 'ok',
  RECEIVED: 'ok',
  SETTLED: 'ok',
  MATCHED: 'ok',
  CLEARED: 'ok',
  APPROVED: 'ok',
  AVAILABLE: 'ok',
  LANDED: 'ok',
  COMPLETED: 'ok',
  PAID: 'ok',
  ACTIVE: 'ok',

  // In flight / needs attention
  PENDING: 'warn',
  PENDING_APPROVAL: 'warn',
  OPEN: 'warn',
  ACCRUED: 'warn',
  LIMITED: 'warn',
  IN_TRANSIT: 'warn',
  UNDER_CLEARANCE: 'warn',
  CONFIRMED: 'warn',
  INVOICED: 'warn',
  DELIVERED: 'warn',
  PARTIAL: 'warn',
  SHIPPED: 'warn',
  ARRIVED: 'warn',
  SUGGESTED: 'warn',
  WARNING: 'warn',
  REPLACEMENT: 'warn',

  // Wrong
  UNMATCHED: 'bad',
  REJECTED: 'bad',
  FAILED: 'bad',
  OUT_OF_SEASON: 'bad',
  VALIDATION_FAILED: 'bad',
  CANCELLED: 'bad',
  REVERSED: 'bad',
  CRITICAL: 'bad',
  SHORT: 'bad',
  WRITTEN_OFF: 'bad',

  // Neutral
  DRAFT: 'mute',
  PLANNED: 'mute',
  LC_OPENED: 'mute',
  FUTURE: 'mute',
  CLOSED: 'mute',
  IGNORED: 'mute',
  NOT_REQUIRED: 'mute',
  CREDIT_NOTE: 'mute',
  CASH_REFUND: 'mute',

  // Batch and expiry monitor: within shelf life.
  OK: 'ok',

  // Audit-trail actions. Tone follows consequence, not severity: POST and
  // APPROVE moved money and settled, CREATE and UPDATE only recorded intent,
  // DELETE removed something. REVAL is the FX job, which nobody authorised.
  CREATE: 'mute',
  UPDATE: 'mute',
  EDIT: 'warn',
  POST: 'ok',
  APPROVE: 'ok',
  DELETE: 'bad',
  REVAL: 'warn',
  SYSTEM: 'mute',
};

/** Enum values are SCREAMING_SNAKE; humans read 'Pending approval'. */
export function humanizeStatus(status: string): string {
  return status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

export interface BadgeProps {
  children?: ReactNode;
  /** Pass a raw enum value to get tone + label for free. */
  status?: string;
  tone?: BadgeTone;
  dot?: boolean;
  className?: string;
}

export function Badge({ children, status, tone, dot, className }: BadgeProps) {
  const resolvedTone: BadgeTone = tone ?? (status ? (STATUS_TONE[status] ?? 'mute') : 'mute');
  const label = children ?? (status ? humanizeStatus(status) : null);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2 py-[3px]',
        'text-[11px] font-medium whitespace-nowrap',
        TONE[resolvedTone],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />}
      {label}
    </span>
  );
}

export interface ChipProps {
  children: ReactNode;
  onRemove?: () => void;
  className?: string;
}

/** Active-filter chip, as shown above every grid in the design. */
export function Chip({ children, onRemove, className }: ChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--line)]',
        'bg-[var(--surface-2)] py-1 pr-1.5 pl-2.5 text-[11.5px] text-[var(--ink-2)]',
        className,
      )}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove filter"
          className="grid size-4 cursor-pointer place-items-center rounded-full border-0 bg-transparent text-[var(--ink-3)] hover:bg-[var(--line)] hover:text-[var(--ink)]"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <path
              d="M1 1l6 6M7 1L1 7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

/** Small monospace tag used in the side rail (JV, TB, GRN …). */
export function Tag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('bb-mono text-[10.5px] text-[var(--ink-3)]', className)}>{children}</span>
  );
}
